import { sceneAssetUrl } from "../scene-core/index.js";

export const BACKGROUND_MEDIA_LIMITS = Object.freeze({
  maxOcclusionWidth: 1920,
  maxOcclusionPixels: 2073600,
  devicePixelRatioCap: 1.5
});

export const DEFAULT_VIDEO_OPTIONS = Object.freeze({
  autoplay: true, loop: true, muted: true, playsInline: true,
  playbackRate: 1, fallbackToImage: true
});

export function normalizedImageConfig(image = {}) {
  const type = image.type === "video" ? "video" : "image";
  return {
    ...image, type, background: image.background || image.poster || "background.png",
    video: image.video || "", poster: image.poster || image.background || "background.png",
    foreground: image.foreground ?? null,
    videoOptions: { ...DEFAULT_VIDEO_OPTIONS, ...(image.videoOptions || {}) }
  };
}

export function chooseMediaQuality(requested = "auto", environment = {}) {
  if (["low", "medium", "high"].includes(requested)) return requested;
  if (environment.reducedMotion) return "low";
  const width = environment.width || 1280, cores = environment.hardwareConcurrency || 4;
  return width <= 1280 || cores < 6 ? "medium" : "high";
}

export class BackgroundMedia {
  constructor({ stage, imageElement, sceneId, image, quality = "auto", forceMotion = false, onStatus = () => {} }) {
    this.stage = stage; this.imageElement = imageElement; this.qualityRequest = quality; this.forceMotion = forceMotion; this.onStatus = onStatus;
    this.frameListeners = new Set(); this.sourceListeners = new Set(); this.disposed = false; this.frameHandle = null; this.rafHandle = null;
    this.state = { type: "image", status: "loading", assetUrl: null, posterUrl: null, fallback: false, error: null,
      autoplay: "not-requested", frameCallbackMode: "none", frames: 0, oneVideoElementCount: 0 };
    this.onVisibility = () => this.handleVisibility(); document.addEventListener("visibilitychange", this.onVisibility);
    this.ready = this.update(sceneId, image);
  }
  onFrame(listener) { this.frameListeners.add(listener); return () => this.frameListeners.delete(listener); }
  onSourceChange(listener) { this.sourceListeners.add(listener); return () => this.sourceListeners.delete(listener); }
  notifySource() { for (const listener of this.sourceListeners) listener(this.source, this); }
  async loadImage(url) {
    const image = this.imageElement; image.src = url;
    if (image.complete && image.naturalWidth) return;
    await new Promise((resolve, reject) => {
      const loaded = () => { cleanup(); resolve(); }, failed = () => { cleanup(); reject(new Error(`Background image failed to load: ${url}`)); };
      const cleanup = () => { image.removeEventListener("load", loaded); image.removeEventListener("error", failed); };
      image.addEventListener("load", loaded); image.addEventListener("error", failed);
    });
  }
  async update(sceneId, rawImage) {
    const updateToken = this.updateToken = (this.updateToken || 0) + 1;
    this.cleanupVideo(); this.sceneId = sceneId; this.config = normalizedImageConfig(rawImage);
    const reducedMotion = !this.forceMotion && globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    this.quality = chooseMediaQuality(this.qualityRequest, { reducedMotion, width: globalThis.innerWidth, hardwareConcurrency: navigator.hardwareConcurrency });
    this.state = { ...this.state, type: this.config.type, status: "loading", fallback: false, error: null, autoplay: "not-requested", frames: 0 };
    this.state.posterUrl = sceneAssetUrl(sceneId, this.config.poster);
    this.state.assetUrl = this.config.type === "video" && this.config.video ? sceneAssetUrl(sceneId, this.config.video) : sceneAssetUrl(sceneId, this.config.background);
    await this.loadImage(this.state.posterUrl).catch(error => { this.state.error = error.message; });
    if (updateToken !== this.updateToken || this.disposed) return this;
    this.imageElement.hidden = false; this.state.status = "poster-ready"; this.notifySource();
    if (this.config.type !== "video" || this.quality === "low" || !this.config.video) {
      this.state.fallback = this.config.type === "video"; this.state.status = this.state.fallback ? "fallback" : "ready"; this.onStatus(this.state); return this;
    }
    const video = document.createElement("video"); this.video = video; video.className = "background-media background-video";
    video.preload = "auto"; video.poster = this.state.posterUrl; video.muted = !!this.config.videoOptions.muted; video.defaultMuted = video.muted;
    video.loop = !!this.config.videoOptions.loop; video.playsInline = !!this.config.videoOptions.playsInline; video.autoplay = !!this.config.videoOptions.autoplay;
    video.playbackRate = this.config.videoOptions.playbackRate; video.setAttribute("aria-hidden", "true"); video.tabIndex = -1;
    video.style.pointerEvents = "none"; this.imageElement.insertAdjacentElement("afterend", video); this.state.oneVideoElementCount = 1;
    video.addEventListener("loadedmetadata", this.onMetadata = () => { video.playbackRate = this.config.videoOptions.playbackRate; this.state.status = "metadata"; this.onStatus(this.state); });
    video.addEventListener("playing", this.onPlaying = () => { this.state.status = "ready"; this.state.fallback = false; this.imageElement.hidden = true; this.scheduleFrames(); this.notifySource(); this.onStatus(this.state); });
    video.addEventListener("pause", this.onPause = () => this.onStatus(this.state));
    video.addEventListener("error", this.onError = () => this.fallback(`Video failed to load or decode: ${this.state.assetUrl}`));
    video.src = this.state.assetUrl; video.load();
    this.onFirstInteraction = () => { if (this.state.autoplay === "waiting-for-user") this.play(); };
    document.addEventListener("pointerdown", this.onFirstInteraction, { once: true, capture: true });
    if (this.config.videoOptions.autoplay) await this.play(true); else { this.state.autoplay = "disabled"; this.state.status = "paused"; }
    return this;
  }
  async play(autoplayAttempt = false) {
    if (!this.video) return false;
    try { await this.video.play(); this.state.autoplay = autoplayAttempt ? "succeeded" : "user-started"; return true; }
    catch (error) { this.state.autoplay = "waiting-for-user"; this.state.status = "waiting-for-user"; this.state.fallback = true; this.state.error = error.message; this.imageElement.hidden = false; this.notifySource(); this.onStatus(this.state); return false; }
  }
  pause() { this.video?.pause(); }
  toggle() { return this.video?.paused ? this.play() : (this.pause(), Promise.resolve(false)); }
  fallback(message) {
    this.state.error = message; this.state.fallback = true; this.state.status = "fallback"; this.imageElement.hidden = false; this.cancelFrames(); this.notifySource(); this.onStatus(this.state);
  }
  get source() { return this.video && !this.state.fallback && this.video.readyState >= 2 ? this.video : this.imageElement; }
  get width() { return this.video?.videoWidth || this.imageElement.naturalWidth || this.config.width || 1536; }
  get height() { return this.video?.videoHeight || this.imageElement.naturalHeight || this.config.height || 1024; }
  scheduleFrames() {
    if (!this.video || this.disposed || this.video.paused || document.hidden || this.frameHandle != null || this.rafHandle != null) return;
    if (typeof this.video.requestVideoFrameCallback === "function") {
      this.state.frameCallbackMode = "requestVideoFrameCallback";
      this.frameHandle = this.video.requestVideoFrameCallback((now, metadata) => { this.frameHandle = null; this.state.frames++; for (const listener of this.frameListeners) listener(now, metadata); this.scheduleFrames(); });
    } else {
      this.state.frameCallbackMode = "requestAnimationFrame";
      let previousTime = this.video.currentTime;
      const tick = now => { this.rafHandle = null; if (this.video.currentTime !== previousTime) { previousTime = this.video.currentTime; this.state.frames++; for (const listener of this.frameListeners) listener(now, null); } this.scheduleFrames(); };
      this.rafHandle = requestAnimationFrame(tick);
    }
  }
  cancelFrames() {
    if (this.frameHandle != null && this.video?.cancelVideoFrameCallback) this.video.cancelVideoFrameCallback(this.frameHandle);
    if (this.rafHandle != null) cancelAnimationFrame(this.rafHandle);
    this.frameHandle = this.rafHandle = null;
  }
  handleVisibility() { if (document.hidden) { this.wasPlaying = !!this.video && !this.video.paused; this.pause(); this.cancelFrames(); } else if (this.wasPlaying) this.play(); }
  debugState() {
    const video = this.video;
    return { ...this.state, quality: this.quality, videoWidth: video?.videoWidth || 0, videoHeight: video?.videoHeight || 0,
      currentTime: video?.currentTime || 0, duration: Number.isFinite(video?.duration) ? video.duration : null, paused: video?.paused ?? true,
      ended: video?.ended ?? false, playbackRate: video?.playbackRate || this.config.videoOptions.playbackRate, readyState: video?.readyState || 0,
      oneVideoElementCount: this.stage.querySelectorAll("video.background-video").length };
  }
  cleanupVideo() {
    this.cancelFrames(); if (!this.video) return;
    this.video.pause(); this.video.removeAttribute("src"); this.video.load(); this.video.remove(); this.video = null; this.state.oneVideoElementCount = 0;
    if (this.onFirstInteraction) document.removeEventListener("pointerdown", this.onFirstInteraction, true);
  }
  dispose() { this.disposed = true; this.cleanupVideo(); document.removeEventListener("visibilitychange", this.onVisibility); this.frameListeners.clear(); this.sourceListeners.clear(); }
}

export function createBackgroundMedia(options) { return new BackgroundMedia(options); }
